import React, {createContext, useContext, useMemo, useState} from 'react';

import {type LayedOutPanel} from '../../../../util/panelTypes';
import {AddVisNewPanelEditor} from '../../../AddVisNewPanelEditor';
import {useWorkspaceInheritedSettingsForPanel} from '../../Settings/hooks/useWorkspaceInheritedSettingsForPanel';
import {useOnOK} from './useOnOK';
type NewPanelEditorContextValue = {
  setNewPanel: (panel: LayedOutPanel) => void;
};
type NewPanelEditorContextProviderProps = {
  children: React.ReactNode;
  groupTitle?: string;
};

const NewPanelEditorContext = createContext<
  NewPanelEditorContextValue | undefined
>(undefined);

export const NewPanelEditorContextProvider = ({
  children,
  groupTitle,
}: NewPanelEditorContextProviderProps) => {
  const inheritedSettings = useWorkspaceInheritedSettingsForPanel();

  const [newPanel, setNewPanel] = useState<LayedOutPanel>();
  const value = useMemo(() => ({setNewPanel}), []);

  const onOK = useOnOK(newPanel, setNewPanel, groupTitle);

  return (
    <NewPanelEditorContext.Provider value={value}>
      {children}

      {newPanel != null && groupTitle && (
        <AddVisNewPanelEditor
          currentHeight={400}
          inheritedSettings={inheritedSettings}
          initialEditingState
          panel={newPanel}
          onCancel={() => {
            setNewPanel(undefined);
          }}
          onOK={onOK}
        />
      )}
    </NewPanelEditorContext.Provider>
  );
};

export const useNewPanelEditorContext = (): NewPanelEditorContextValue => {
  const value = useContext(NewPanelEditorContext);
  if (value == null) {
    throw new Error(
      'useNewPanelEditorContext must be used within a NewPanelEditorContextProvider'
    );
  }
  return value;
};
